/**
 * Digital Student Passport — a single lifelong record for a student.
 * Consumed by parents, universities and inspectors.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  BookOpen, Trophy, Award, ShieldCheck, Users, Sparkles, Hash, Fingerprint,
  QrCode, ExternalLink, Loader2, GraduationCap, HeartHandshake, Wallet,
  FileBadge, CheckCircle2, ArrowLeft,
} from "lucide-react";
import client, { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Section, KPITile, Loading, DataTable, StatusPill } from "../components/Common";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function DigitalPassport() {
  const { school, id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studentPicker, setStudentPicker] = useState([]);

  const load = async (studentId) => {
    setLoading(true);
    try {
      const { data } = await client.get(`/passport/${studentId}`);
      setPassport(data);
    } catch (e) {
      setPassport(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    // If a specific id is provided, load it; otherwise resolve parent's first child
    if (id) {
      load(id);
    } else if (user?.role === "parent") {
      client.get("/passport/parent/children").then(({ data }) => {
        setStudentPicker(data.passports || []);
        if ((data.passports || []).length > 0) {
          setPassport(data.passports[0]);
        }
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [id, user?.role]);

  if (loading) return <Loading label="Loading Digital Student Passport…" />;
  if (!passport) {
    return (
      <div className="p-8">
        <PageHeader kicker="Digital Student Passport" title="Select a student" subtitle="Pick a student to open their lifelong digital passport." />
        <Link to={`/education360/${school}/students`} className="hl-btn-outline inline-flex items-center gap-2 text-sm px-3 py-2">
          <ArrowLeft className="w-4 h-4" /> Back to students
        </Link>
      </div>
    );
  }

  const s = passport.student;
  const school_info = passport.school;
  const h = passport.headline;
  const subjectRows = (passport.academic?.subjects || []).map((s) => ({ subject: s.subject, avg: s.score || 0 }));

  return (
    <div data-testid="passport-page">
      {/* Cover */}
      <div className="relative overflow-hidden rounded-xl mb-6 shadow-2xl"
           style={{ background: `linear-gradient(135deg, ${school_info?.primary_color || "#002147"} 0%, ${school_info?.primary_color || "#001432"} 55%, #000a1c 100%)` }}
           data-testid="passport-cover">
        <div className="absolute inset-0 opacity-20 pointer-events-none" aria-hidden>
          {Array.from({ length: 20 }).map((_, i) => (
            <span key={i} className="absolute rounded-full bg-white/30"
              style={{ width: `${3 + (i % 4)}px`, height: `${3 + (i % 4)}px`,
                left: `${(i * 51) % 100}%`, top: `${(i * 29) % 100}%`,
                animation: `edu-float ${18 + i}s ease-in-out ${i * 1.1}s infinite` }} />
          ))}
        </div>
        <div className="relative p-8 sm:p-10 text-white grid md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--hl-orange)] font-bold flex items-center gap-1.5">
              <FileBadge className="w-3 h-3" /> Digital Student Passport · Education360™
            </div>
            <div className="mt-3 flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-white/10 border-2 border-[color:var(--hl-orange)] grid place-items-center text-2xl font-display font-bold">
                {(s.first_name || "?")[0]}{(s.last_name || "?")[0]}
              </div>
              <div>
                <div className="font-display font-bold text-3xl sm:text-4xl tracking-tight leading-tight">
                  {s.first_name} {s.last_name}
                </div>
                <div className="text-white/70 text-sm mt-1">
                  {s.year_group} · {s.house} · {s.boarding ? "Boarder" : "Day scholar"}
                  {s.scholarship && ` · ${s.scholarship} scholar`}
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3 text-[11px]">
              <MetaChip label="Admission" value={s.admission_no} />
              <MetaChip label="Nationality" value={s.nationality} />
              <MetaChip label="Date of birth" value={s.date_of_birth} />
              <MetaChip label="Since" value={(s.joined_on || "").slice(0, 10)} />
              <MetaChip label="Status" value={s.status} />
            </div>
          </div>
          <div className="rounded-xl p-5 bg-white/5 border border-white/10 backdrop-blur">
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/50">Verify authenticity</div>
            <div className="mono text-xs mt-1 break-all">{passport.passport_id}</div>
            <div className="text-[10px] text-white/40 mt-4 flex items-center gap-1.5"><Fingerprint className="w-3 h-3" /> Seal {passport.integrity_seal}</div>
        {passport.verification?.qr_data_uri && (
          <div className="w-24 h-24 rounded bg-white p-1.5 mt-3">
            <img src={passport.verification.qr_data_uri} alt="Verification QR" className="w-full h-full" data-testid="passport-qr" />
          </div>
        )}
            <Link
              to={`/education360/verify/passport/${passport.passport_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-xs px-3 py-2 rounded bg-[color:var(--hl-orange)] hover:brightness-110 transition"
              data-testid="btn-verify-passport"
            >
              <QrCode className="w-3.5 h-3.5" /> Verify passport <ExternalLink className="w-3 h-3" />
            </Link>
            <div className="text-[10px] text-white/40 mt-3">{school_info?.name} · {school_info?.country}</div>
          </div>
        </div>
      </div>

      {/* Student picker (parents with multiple children) */}
      {user?.role === "parent" && studentPicker.length > 1 && (
        <div className="hl-card p-3 mb-5 flex flex-wrap gap-2" data-testid="student-picker">
          {studentPicker.map((p) => (
            <button
              key={p.passport_id}
              onClick={() => setPassport(p)}
              data-testid={`pick-${p.student.id}`}
              className={`text-xs px-3 py-1.5 rounded border transition ${p.passport_id === passport.passport_id ? "bg-[color:var(--hl-navy)] text-white border-[color:var(--hl-navy)]" : "bg-white border-slate-200 hover:border-slate-300"}`}
            >
              {p.student.first_name} {p.student.last_name} · {p.student.year_group}
            </button>
          ))}
        </div>
      )}

      {/* Headline KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPITile testId="pass-academic" tone="dark" label="Academic avg" value={`${h.academic_avg}%`} sub="Weighted across subjects" />
        <KPITile testId="pass-attendance" label="Attendance" value={`${h.attendance_pct}%`} sub={s.status === "enrolled" ? "This academic year" : "Latest recorded"} />
        <KPITile testId="pass-merits" label="Merits" value={h.merits} sub={`${h.demerits} demerits · ${h.house_points} pts`} />
        <KPITile testId="pass-fee" label="Fee status" value={<span className="capitalize">{h.fee_status}</span>} accent={h.fee_status === "settled" ? "In good standing" : "Outstanding balance"} />
      </div>

      {/* Academic + Attendance chart */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="hl-card p-5 lg:col-span-2" data-testid="passport-academic">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline">Academic profile</div>
              <div className="font-display font-bold text-lg">Subject performance</div>
            </div>
            <BookOpen className="w-5 h-5 text-slate-400" />
          </div>
          {subjectRows.length > 0 ? (
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={subjectRows} layout="vertical">
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} fontSize={11} />
                  <YAxis type="category" dataKey="subject" width={110} fontSize={11} />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Bar dataKey="avg" fill="#002147" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-slate-400 py-10 text-center">No graded assessments recorded yet.</div>
          )}
        </div>
        <div className="hl-card p-5" data-testid="passport-headline-tiles">
          <div className="overline">Recognition</div>
          <div className="font-display font-bold text-lg mb-4">A summary of achievement</div>
          <div className="space-y-3">
            <MiniRow icon={Award} label="Awards" value={h.awards} accent="#D9531E" />
            <MiniRow icon={Trophy} label="Activities" value={h.activities} accent="#059669" />
            <MiniRow icon={GraduationCap} label="Leadership roles" value={h.leadership_roles} accent="#7c3aed" />
            <MiniRow icon={Users} label="House points" value={h.house_points} accent="#0284c7" />
          </div>
        </div>
      </div>

      {/* Sections */}
      <Section title="Awards & recognitions" subtitle="Full record of certificates and prizes">
        {(passport.studentlife?.awards || []).length === 0 ? (
          <div className="hl-card p-5 text-sm text-slate-500">No awards recorded yet.</div>
        ) : (
          <div className="hl-card overflow-hidden">
            <table className="hl-table w-full text-sm" data-testid="passport-awards-table">
              <thead><tr><th>Category</th><th>Title</th><th>Term</th><th>Awarded</th></tr></thead>
              <tbody>
                {passport.studentlife.awards.map((a) => (
                  <tr key={a.id}>
                    <td className="capitalize"><span className="text-xs font-semibold text-[color:var(--hl-orange)]">{a.category}</span></td>
                    <td>{a.title}</td>
                    <td className="text-xs">{a.term}</td>
                    <td className="text-xs text-slate-500">{(a.awarded_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Activities & societies" subtitle="Sport, arts, service, and enrichment">
        {(passport.studentlife?.activities || []).length === 0 ? (
          <div className="hl-card p-5 text-sm text-slate-500">Not yet enrolled in a club or society.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="passport-activities">
            {passport.studentlife.activities.map((a, i) => (
              <div key={i} className="hl-card p-4">
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--hl-orange)] font-semibold">{a.kind}</div>
                <div className="font-display font-bold text-base mt-0.5">{a.name}</div>
                <div className="text-xs text-slate-500 mt-1">Coach · {a.coach || "TBD"} · {a.day} {a.time}</div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Leadership" subtitle="Prefectships, house captaincies and student council">
        {(passport.studentlife?.leadership || []).length === 0 ? (
          <div className="hl-card p-5 text-sm text-slate-500">No current leadership appointments.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {passport.studentlife.leadership.map((l) => (
              <div key={l.id} className="hl-card p-4 flex items-start gap-3">
                <GraduationCap className="w-5 h-5 text-[color:var(--hl-orange)] mt-0.5" />
                <div>
                  <div className="font-semibold">{l.role}</div>
                  <div className="text-xs text-slate-500">Scope · {l.scope} · AY {l.academic_year}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Financial standing" subtitle="Consolidated tuition, boarding and examinations">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPITile label="Billed" value={money(passport.finance?.billed_usd)} />
          <KPITile label="Paid" tone="dark" value={money(passport.finance?.paid_usd)} />
          <KPITile label="Outstanding" value={money(passport.finance?.outstanding_usd)} />
          <KPITile label="Status" value={<span className="capitalize">{passport.finance?.status}</span>} accent={passport.finance?.status === "settled" ? "In good standing" : "Action required"} />
        </div>
      </Section>

      <Section title="Documents & credentials" subtitle="Reports, transcripts and certificates archived to date">
        {(passport.documents || []).length === 0 ? (
          <div className="hl-card p-5 text-sm text-slate-500">No documents archived yet.</div>
        ) : (
          <div className="hl-card overflow-hidden">
            <table className="hl-table w-full text-sm" data-testid="passport-docs-table">
              <thead><tr><th>Type</th><th>Label</th><th>Term</th><th>Version</th><th>Verification</th></tr></thead>
              <tbody>
                {passport.documents.map((d) => (
                  <tr key={d.id}>
                    <td className="text-xs uppercase font-semibold text-[color:var(--hl-orange)]">{(d.document_type || "").replace(/_/g, " ")}</td>
                    <td>{d.subject_label}</td>
                    <td className="text-xs">{d.period} · {d.academic_year}</td>
                    <td>v{d.version}</td>
                    <td className="text-xs">
                      <Link to={`/education360/verify/${d.verification_id}`} target="_blank" rel="noopener noreferrer" className="text-[color:var(--hl-orange)] hover:underline inline-flex items-center gap-1">
                        <Hash className="w-3 h-3" /> {d.verification_id}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <div className="mt-6 text-[10px] uppercase tracking-[0.28em] text-slate-400 text-center flex items-center justify-center gap-2" data-testid="passport-footer">
        <ShieldCheck className="w-3 h-3 text-emerald-500" /> Issued by Education360™ · {school_info?.name} · Passport ID {passport.passport_id}
      </div>
    </div>
  );
}

function MetaChip({ label, value }) {
  return (
    <span className="inline-flex flex-col leading-none px-3 py-2 rounded bg-white/5 border border-white/10">
      <span className="text-[9px] uppercase tracking-[0.24em] text-white/50">{label}</span>
      <span className="text-white text-xs mt-0.5 font-semibold">{value || "—"}</span>
    </span>
  );
}

function MiniRow({ icon: Icon, label, value, accent }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-md grid place-items-center flex-shrink-0" style={{ background: `${accent}12`, color: accent }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="font-display font-bold text-lg">{value}</div>
      </div>
    </div>
  );
}
